import React from "react";
import { VakTestData } from "../../typings/vak";
import { Lightbulb, CheckCircle, Info, Sparkles } from "lucide-react";
import { formatBold } from "../../utils/vak";

interface VakAiRecommendationProps {
  testData: VakTestData;
}

const VakAiRecommendation: React.FC<VakAiRecommendationProps> = ({ testData }) => {
  if (!testData.ai_generated || !testData.ai_summary) {
    return null;
  }

  const parseJsonArray = (data: string | string[] | undefined): string[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    try {
      return JSON.parse(data);
    } catch (e) {
      console.error("Failed to parse AI data:", e);
      return [];
    }
  };

  const strategies = parseJsonArray(testData.ai_strategies);
  const tips = parseJsonArray(testData.ai_tips);

  return (
    <div className="mt-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 shadow-sm overflow-hidden">
      <div className="bg-indigo-600 px-4 py-3 flex items-center gap-2">
        <Sparkles className="text-white w-5 h-5" />
        <h3 className="text-white font-bold text-lg">Hasil Analisis</h3>
      </div>
      
      <div className="p-3">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 bg-white p-4 rounded-lg border border-indigo-100 shadow-sm">
            <div className="text-indigo-600 text-xs font-bold uppercase tracking-wider mb-1">Gaya Belajar</div>
            <div className="text-2xl font-black text-indigo-900">{testData.ai_type || "Terdeteksi"}</div>
          </div>
          <div className="flex-[2] bg-white p-4 rounded-lg border border-indigo-100 shadow-sm">
            <div className="text-indigo-600 text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
              <Info className="w-3 h-3" /> Ringkasan Analisis
            </div>
            <p 
              className="text-gray-700 text-sm leading-relaxed italic"
              dangerouslySetInnerHTML={{ __html: `"${formatBold(testData.ai_summary)}"` }}
            />
          </div>
        </div>

        <div className="space-y-6">
          <section>
            <h4 className="flex items-center gap-2 text-indigo-900 font-bold mb-3">
              <Lightbulb className="text-amber-500 w-5 h-5" />
              Strategi Belajar yang Disarankan
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {strategies.map((strategy, index) => (
                <div key={index} className="flex gap-3 bg-white p-3 rounded-lg border border-indigo-50 shadow-sm">
                  <div className="bg-indigo-100 text-indigo-600 font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs mt-0.5">
                    {index + 1}
                  </div>
                  <p 
                    className="text-gray-700 text-sm"
                    dangerouslySetInnerHTML={{ __html: formatBold(strategy) }}
                  />
                </div>
              ))}
            </div>
          </section>

          {tips.length > 0 && (
            <section className="bg-white/50 p-4 rounded-lg border border-indigo-100/50">
              <h4 className="flex items-center gap-2 text-indigo-900 font-bold mb-3 text-sm">
                <CheckCircle className="text-green-500 w-4 h-4" />
                Tips untuk Orang Tua & Guru
              </h4>
              <ul className="space-y-2">
                {tips.map((tip, index) => (
                  <li key={index} className="flex gap-2 text-gray-700 text-sm">
                    <span className="text-green-500 font-bold">•</span>
                    <span dangerouslySetInnerHTML={{ __html: formatBold(tip) }} />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-indigo-400 italic">
            * Rekomendasi ini dihasilkan oleh kecerdasan buatan untuk membantu eksplorasi cara belajar Anda.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VakAiRecommendation;
